/**
 * Chart Tooltip rendered via React Portal to document.body.
 * Ensures tooltip always appears on top (z-index 10000), never clipped by
 * header, dropdown, or chart card overflow/stacking context.
 *
 * Uses same content as ChartTooltip (filter non-zero, total, opaque bg).
 * Position: fixed from chart container + Recharts coordinate; placed above cursor.
 */

import { createPortal } from 'react-dom';

function TooltipContent({ label, filtered, total, formatValue }) {
  return (
    <div style={styles.tooltip}>
      <div style={styles.label}>{label}</div>
      <div style={styles.total}>Total: {formatValue(total)}</div>
      <div style={styles.breakdown}>
        {filtered.map((entry, index) => {
          const value = entry.value || 0;
          const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          return (
            <div key={index} style={styles.breakdownItem}>
              <div style={styles.breakdownHeader}>
                <span style={{ ...styles.channelDot, backgroundColor: entry.color }} />
                <span style={styles.channelName}>{entry.name}</span>
              </div>
              <div style={styles.breakdownValue}>
                {formatValue(value)} ({pct}%)
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ChartTooltipPortal({
  active,
  payload,
  label,
  coordinate,
  chartType = 'revenue',
  containerRef,
}) {
  if (!active || !payload || payload.length === 0) return null;

  // Sentinel keys used by Recharts for LabelList positioning (transparent bar); must never show in tooltip
  const SENTINEL_KEYS = ['_zero', '__labelListAnchor', '__total_anchor'];
  const isSentinel = (e) => {
    const key = (e.dataKey ?? e.name ?? '').toString().trim().toLowerCase();
    return SENTINEL_KEYS.some((s) => key === s.toLowerCase());
  };

  const filtered = payload.filter((e) => {
    if (isSentinel(e)) return false;
    const val = e.value ?? 0;
    if (val <= 0) return false;
    // Also skip effectively-zero series (e.g. 1e-9 from _zero) to avoid "$0 (0.0%)" clutter
    if (typeof val === 'number' && val < 0.01 && val > 0) return false;
    return true;
  });
  if (filtered.length === 0) return null;

  const total = filtered.reduce((s, e) => s + (e.value || 0), 0);
  const formatValue = (v) =>
    chartType === 'revenue'
      ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v)
      : String(Math.round(v));

  const rect = containerRef?.current?.getBoundingClientRect();
  if (!rect || coordinate == null) return null;

  const x = rect.left + (coordinate.x ?? 0);
  const y = rect.top + (coordinate.y ?? 0);

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left: x,
        top: y,
        transform: 'translate(-50%, -100%) translateY(-12px)',
        zIndex: 10000,
        pointerEvents: 'none',
      }}
    >
      <TooltipContent label={label} filtered={filtered} total={total} formatValue={formatValue} />
    </div>,
    document.body
  );
}

const styles = {
  tooltip: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '12px 16px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
    minWidth: '200px',
    fontSize: '12px',
    fontWeight: 500,
  },
  label: { fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' },
  total: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '1px solid #e5e7eb',
  },
  breakdown: { display: 'flex', flexDirection: 'column', gap: '8px' },
  breakdownItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
  breakdownHeader: { display: 'flex', alignItems: 'center', gap: '8px' },
  channelDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  channelName: { fontSize: '12px', fontWeight: 500, color: '#374151' },
  breakdownValue: { fontSize: '12px', fontWeight: 500, color: '#6b7280', marginLeft: '16px' },
};
